import React, { Fragment } from 'react';
import { isEmpty, map } from 'lodash';
import { ColorBlend } from '../../../utils/color-blend.js';
import {
   COLOR_BACKGROUND,
   COLOR_ADD_DIM,
   COLOR_QUALITATIVE_CATEGORICAL,
} from '../chart-colors.js';
import {
   BRUSH_POSITION_START,
   CHART_X_AXIS_PADDING_LEFT,
   CHART_X_AXIS_PADDING_RIGHT,
   BRUSH_INITIAL_DATA_LENGTH,
   getBrushProps,
} from '../chart-util.js';

export const getBarChartStackedGroupedConfig = (configChart, chartData) => {
   const {
      chartWidth = '100%',
      chartHeight = 300,
      configBars: {
         plotBars = [],
         barSize = 20,
         isStackedBars = false,
         stackedValues = [],
         isHighLightBarOnClick = false,
         isHighLightCellOnClick = false,
         barStrokes = COLOR_QUALITATIVE_CATEGORICAL,
         barStrokeIndex = plotBars.length < 4 ? '3' : plotBars.length,
         isBarRoundedEdge = false,
         isSetBarBackground = false,
         barBackgroundColor = isSetBarBackground
            ? COLOR_BACKGROUND
            : 'transparent',
         barOnClickHandler = () => {},
      },
      configXAxisGroup: {
         xGroupAxisKey = null,
         xGroupAxisTickFormatter = (tick) => tick,
         xGroupAxisTick = defaultGroupAxisTick,
         xGroupAxisHeight = 70,
         xGroupAxisTickMargin = 30,
      } = {},
      configYAxis: { yAxisTickFormatter },
      configLegends: {
         legendLabels,
         legendIcon = isBarRoundedEdge ? 'circle' : 'square',
         isLegendTextColorSameAsChartColor = false,
         isToggleChartOnClickOfLegend = false,
         isGroupLegend = false,
      } = {},
      isVisibleHorizontalGrid = true,
      isBrushVisible = false,
      tooltipContent,
      /* TODO: create custom tooltip */
      // tooltipContent = <CustomTooltip />
   } = configChart || {};

   return {
      chartWidth,
      chartHeight,
      plotBars,
      getBarKeys: getBarKeys,
      getGroupBarKeys: getGroupBarKeys,
      isHighLightBarOnClick,
      isHighLightCellOnClick,
      isStackedBars,
      stackedValues,
      barSize,
      barStrokes: barStrokes[barStrokeIndex]
         ? barStrokes[barStrokeIndex]
         : barStrokes['default'],
      isBarRoundedEdge,
      barBackgroundColor,
      barOnClickHandler,
      xGroupAxisKey,
      xGroupAxisTickFormatter,
      xGroupAxisTick,
      xGroupAxisHeight,
      xGroupAxisTickMargin,
      yAxisTickFormatter,
      legendLabels,
      legendIcon,
      isGroupLegend,
      legendTextColor: isLegendTextColorSameAsChartColor ? '' : '#000000',
      isToggleChartOnClickOfLegend,
      isVisibleHorizontalGrid,
      tooltipContent,
      brush: isBrushVisible
         ? {
              ...getBrushProps(
                 BRUSH_POSITION_START,
                 BRUSH_INITIAL_DATA_LENGTH,
                 chartData.length
              ),
              tickFormatter: (tickValue) => tickValue,
              brushHeight: 20,
           }
         : {},
      getBarEdge: (barKeys, isRounded) => (barRank) => {
         if (!isRounded) {
            return [0, 0, 0, 0];
         }
         if (barKeys.length === 1 || barRank === -1 || !isStackedBars) {
            return [barSize, barSize, barSize, barSize];
         }

         switch (barRank) {
            case 0:
               return [0, 0, barSize, barSize];
            case barKeys.length - 1:
               return [barSize, barSize, 0, 0];
            default:
               return [0, 0, 0, 0];
         }
      },
   };
};

export const getBarCellBackgroundFill =
   (isStackedBars, barBackgroundColor, getBarEdgesStyle) => (barIterateIndex) =>
      !isStackedBars || barIterateIndex === 0
         ? { fill: barBackgroundColor, radius: getBarEdgesStyle(-1) }
         : null;

export const getBarCellFill =
   (selectedBarData, isHighLightOnClickOfBar, isHighLightCellOnClick) =>
   (cellIndex, cellId, fillColor) => {
      if (
         isEmpty(selectedBarData) ||
         (!isHighLightOnClickOfBar && !isHighLightCellOnClick)
      ) {
         return fillColor;
      }

      const dimBarColor = ColorBlend.mix(
         fillColor,
         COLOR_ADD_DIM.colorMix,
         COLOR_ADD_DIM.weight
      );
      if (isHighLightCellOnClick) {
         return selectedBarData.cellId !== cellId ? dimBarColor : fillColor;
      } else if (isHighLightOnClickOfBar) {
         return selectedBarData.cellIndex === cellIndex
            ? fillColor
            : dimBarColor;
      }

      return fillColor;
   };

const getBarKeys = (pBars) => map(pBars, (pBar) => pBar);

const getGroupBarKeys = (pStackedBars) => pStackedBars;

const defaultGroupAxisTick = (data) => (tickProps) => {
   if (isEmpty(data)) {
      return;
   }

   const {
      x,
      y,
      payload,
      index: dataIndex,
      visibleTicksCount,
      width,
   } = tickProps;

   const { value, offset } = payload;
   const chartOffset = getBarChartOffset(width, visibleTicksCount);
   // const isShowGroupedTick = dataIndex !== 0 && data[dataIndex + 1] && value !== data[dataIndex + 1].group;
   // const textIndex = dataIndex === 0 ? dataIndex : (dataIndex + 1);
   // const svgWidth = getSVGWidth(data[textIndex] && data[textIndex].groupSize, chartOffset);
   const pathX = Math.floor(x + offset);

   const path = <path d={`M${pathX},${y}v${-38}`} stroke='gray' />;

   const svg = (
      <svg width={width} y={y} x={x}>
         <text
            x={x}
            y={y}
            // alignmentBaseline='middle'
            textAnchor='middle'
            fontSize={14}
         >
            {value}
         </text>
      </svg>
   );

   return (
      <Fragment>
         {svg}
         {path}
      </Fragment>
   );
};

const getBarChartOffset = (chartWidth, numberOfTicks) =>
   (chartWidth - (CHART_X_AXIS_PADDING_LEFT + CHART_X_AXIS_PADDING_RIGHT)) /
   numberOfTicks;

const getSVGWidth = (groupSize, chartOffset) => groupSize * chartOffset;
